/**
 * ALDER SEARCH Sectors Section
 * Design: "Alder Forest Organic Precision"
 * Warm stone background, horizontal scrollable sector tiles on mobile
 * Staggered reveal, brass accent on hover
 */

import { useEffect, useRef } from "react";

const sectors = [
  {
    icon: "⬡",
    title: "Technology & Software",
    description: "SaaS, enterprise software, cybersecurity, data & analytics, and infrastructure technology backed by venture, growth, and buyout sponsors.",
    roles: ["CPO", "Head of Product", "VP Product", "Staff / Principal Product Manager", "Lead Product Manager"],
  },
  {
    icon: "⬡",
    title: "Financial Services & Fintech",
    description: "Fintech, payments infrastructure, insurance, wealth management, and specialty finance at every stage from VC to PE ownership.",
    roles: ["CPO", "Head of Product", "VP Product", "Staff / Principal Product Manager", "Lead Product Manager"],
  },
  {
    icon: "⬡",
    title: "Healthcare & Life Sciences",
    description: "Healthcare services, medical devices, biopharma, digital health, and healthcare IT across venture-backed, mid-market, and large-cap PE.",
    roles: ["CPO", "Head of Product", "VP Product", "Staff / Principal Product Manager", "Lead Product Manager"],
  },
  {
    icon: "⬡",
    title: "Energy & Commodities",
    description: "Energy, utilities, infrastructure, and commodities businesses undergoing VC- and PE-driven transformation.",
    roles: ["CPO", "Head of Product", "VP Product", "Staff / Principal Product Manager", "Lead Product Manager"],
  },
];

export default function SectorsSection() {
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const allEls = [headerRef.current, ...cardsRef.current];
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    allEls.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      el.style.transition = `opacity 0.65s ease-out ${i * 80}ms, transform 0.65s ease-out ${i * 80}ms`;
      observer.observe(el);
    });


    // Fallback: ensure all elements become visible after 2s regardless
    const fallback = setTimeout(() => {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <section
      id="sectors"
      className="py-28"
      style={{ background: "oklch(0.952 0.012 80)" }}
    >
      <div className="container">
        {/* Section Header */}
        <div ref={headerRef} className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div
                className="h-px w-10"
                style={{ background: "oklch(0.62 0.085 75)" }}
              />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.18em",
                }}
              >
                Sector Coverage
              </span>
            </div>
            <h2
              className="font-display"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4vw, 3.2rem)",
                color: "oklch(0.245 0.065 155)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              Deep Expertise Across
              <br />
              Every VC &amp; PE Sector
            </h2>
          </div>
          <p
            className="font-body max-w-sm"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 400,
              fontSize: "0.95rem",
              lineHeight: 1.75,
              color: "oklch(0.45 0.015 155)",
            }}
          >
            We recruit product people in four sectors: technology and software, financial services and fintech, healthcare and life sciences, and energy and commodities.
          </p>
        </div>

        {/* Sectors Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sectors.map((sector, i) => (
            <div
              key={sector.title}
              ref={(el) => { cardsRef.current[i] = el; }}
              className="group p-6 relative overflow-hidden card-lift"
              style={{
                background: "white",
                border: "1px solid oklch(0.88 0.018 80)",
                borderRadius: "4px",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "oklch(0.245 0.065 155)";
                const title = e.currentTarget.querySelector(".sector-title") as HTMLElement;
                const desc = e.currentTarget.querySelector(".sector-desc") as HTMLElement;
                const tags = e.currentTarget.querySelectorAll(".sector-tag") as NodeListOf<HTMLElement>;
                if (title) title.style.color = "oklch(0.952 0.012 80)";
                if (desc) desc.style.color = "oklch(0.75 0.02 155)";
                tags.forEach(tag => {
                  tag.style.background = "oklch(0.32 0.07 155)";
                  tag.style.color = "oklch(0.72 0.09 75)";
                });
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "white";
                const title = e.currentTarget.querySelector(".sector-title") as HTMLElement;
                const desc = e.currentTarget.querySelector(".sector-desc") as HTMLElement;
                const tags = e.currentTarget.querySelectorAll(".sector-tag") as NodeListOf<HTMLElement>;
                if (title) title.style.color = "oklch(0.245 0.065 155)";
                if (desc) desc.style.color = "oklch(0.45 0.015 155)";
                tags.forEach(tag => {
                  tag.style.background = "oklch(0.952 0.012 80)";
                  tag.style.color = "oklch(0.32 0.07 155)";
                });
              }}
            >
              {/* Brass accent top border */}
              <div
                className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: "oklch(0.62 0.085 75)" }}
              />

              <h3
                className="sector-title font-display mb-3 transition-colors duration-300"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "oklch(0.245 0.065 155)",
                  lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                }}
              >
                {sector.title}
              </h3>
              <p
                className="sector-desc font-body mb-4 transition-colors duration-300"
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontWeight: 400,
                  fontSize: "0.82rem",
                  lineHeight: 1.7,
                  color: "oklch(0.45 0.015 155)",
                }}
              >
                {sector.description}
              </p>

            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
